'use client';

import { ReactNode, createElement, createContext, useContext } from 'react';

export type IconPack = 'default' | 'lucide' | 'phosphor';
export type BrandPack = 'original' | 'branded';

export type CustomIconEntry = {
    /** Sanitizovaný inline SVG markup. */
    svg: string;
    /** Původní asset v knihovně (provenance). */
    assetId?: string;
    /** Název souboru pro zobrazení v editoru. */
    fileName?: string;
};

export type IconsConfig = {
    pack?: IconPack;
    overrides?: Partial<Record<string, IconPack>>;
    customIcons?: Partial<Record<string, CustomIconEntry>>;
    brandPack?: BrandPack;
    brandOverrides?: Partial<Record<string, BrandPack>>;
};

type IconConfigContextValue = {
    pack: IconPack;
    overrides: Partial<Record<string, IconPack>>;
    customIcons: Partial<Record<string, CustomIconEntry>>;
    // Volitelné záměrně (na rozdíl od `pack`/`overrides`) — vyhne se kaskádě tsc
    // chyb v souborech, které konstruují context value bez brand polí.
    // `resolveBrandPack` má `?? 'original'` fallback; context default + provider je vždy dodají.
    brandPack?: BrandPack;
    brandOverrides?: Partial<Record<string, BrandPack>>;
};

export const IconConfigContext = createContext<IconConfigContextValue>({
    pack: 'default',
    overrides: {},
    customIcons: {},
    brandPack: 'original',
    brandOverrides: {},
});

export const useIconConfig = () => useContext(IconConfigContext);

export const resolveIconPack = (name: string, config: IconConfigContextValue): IconPack => {
    return (config.overrides[name] as IconPack | undefined) ?? config.pack;
};

export const resolveBrandPack = (name: string, config: IconConfigContextValue): BrandPack => {
    return (config.brandOverrides?.[name] as BrandPack | undefined) ?? config.brandPack ?? 'original';
};

type IconConfigProviderProps = {
    icons?: IconsConfig;
    children?: ReactNode;
};

export const IconConfigProvider = ({ icons, children }: IconConfigProviderProps) => {
    const value: IconConfigContextValue = {
        pack: icons?.pack ?? 'default',
        overrides: icons?.overrides ?? {},
        customIcons: icons?.customIcons ?? {},
        brandPack: icons?.brandPack ?? 'original',
        brandOverrides: icons?.brandOverrides ?? {},
    };
    return createElement(IconConfigContext.Provider, { value }, children);
};
